import React from "react";
import { useState, useMemo } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import companyTickers from "./processed_company_tickers.json";
import Fuse from "fuse.js";

import exactSymbolMatch from "./exactSymbolMatch";
import SymbolOption from "./SymbolOption";

const maxSelectOptions = 5;

const fuseOptions = {
  isCaseSensitive: false,
  minMatchCharLength: 2,
  threshold: 0.35,
  keys: ["symbol", "name"], // search by symbol and name
};

const fuse = new Fuse(companyTickers, fuseOptions);

function filterOptions(options: SymbolOption[], { inputValue }: { inputValue: string }) {
  return fuse
    .search(inputValue)
    .map((option) => option.item)
    .slice(0, maxSelectOptions);
}

type SymbolAutocompleteProps = {
  setSymbol: (newSymbol: string | null) => void;
  setName?: (newValue: string | null) => void;
  symbol: string | null; //
  TextFieldProps?: any;
};

function getOptionLabel(option: string | SymbolOption) {
  return typeof option === "string" ? option : "(" + option.symbol + ") " + option.name;
}

function SymbolAutocomplete(props: SymbolAutocompleteProps) {
  const [inputValue, setInputValue] = useState("");

  const exactSymbolOption = useMemo(() => exactSymbolMatch(props.symbol), [props.symbol]);

  const exactOptionLabel = useMemo(
    () => (exactSymbolOption === undefined ? undefined : getOptionLabel(exactSymbolOption)),
    [exactSymbolOption]
  );

  return (
    <Autocomplete
      // disablePortal
      freeSolo={inputValue ? false : true}
      options={companyTickers}
      getOptionLabel={getOptionLabel}
      autoHighlight
      autoSelect
      disabled={props.TextFieldProps && props.TextFieldProps.disabled}
      selectOnFocus
      handleHomeEndKeys
      filterOptions={filterOptions}
      fullWidth
      renderInput={(params) => {
        return (
          <TextField
            label="Symbol"
            {...props.TextFieldProps}
            onKeyDown={
              props.TextFieldProps.onKeyDown === undefined
                ? undefined
                : (e: KeyboardEvent) => {
                    if (exactSymbolOption !== undefined && inputValue === exactOptionLabel) {
                      props.TextFieldProps.onKeyDown(e);
                    }
                  }
            }
            {...params}
          />
        );
      }}
      value={props.symbol ? exactSymbolOption : null}
      onChange={(_, value) => {
        if (typeof value === "string") return;
        props.setSymbol(value && value.symbol);
        props.setName?.(value && value.name);
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      noOptionsText={
        exactSymbolOption !== undefined && inputValue === exactOptionLabel
          ? "Found a matching ticker or company. If this is not the company or ticker you expected, please try searching again or contact us for help. "
          : "No matching companies or tickers. Please check your spelling or contact us for help."
      }
    />
  );
}

export default SymbolAutocomplete;
